import { AlertMessage, useAlertConfirm } from "../../components/alerts";
import { useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  Users,
  UserRoundCheck,
  UserRound,
  Search,
  Plus,
  KeyRound,
  Trash2,
  ShieldCheck,
  Check,
  X,
} from "lucide-react";
import type { TeamDto, TeamWithPasswordDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { Button } from "../../components/motion/button/base";
import { Input } from "../../components/motion/input";
import {
  CenterMorphModal,
  CenterMorphModalTrigger,
  CenterMorphModalContent,
  CenterMorphModalClose,
} from "../../components/motion/center-morph-modal";
import { Switch } from "../../components/motion/switch";
import { Table, type TableColumn } from "../../components/motion/table";
import { Identicon } from "../../components/Identicon.js";
import { tableHeight } from "../../components/domain.js";

const fields = {
  field: "rounded-xl bg-background",
  label: "px-0",
  input: "placeholder:text-muted-foreground",
};
function Feedback({ children, error = false }: { children: ReactNode; error?: boolean }) {
  return <AlertMessage tone={error ? "error" : "info"}>{children}</AlertMessage>;
}
const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Não foi possível concluir a operação.";

export function AdminTeamsPage(): React.JSX.Element {
  const confirmAlert = useAlertConfirm();

  const queryClient = useQueryClient();
  const [credential, setCredential] = useState<TeamWithPasswordDto | null>(null);
  const [search, setSearch] = useState("");
  const credentialPanel = useRef<HTMLDivElement>(null);
  const teams = useQuery({
    queryKey: ["admin-teams"],
    queryFn: () => api.get<TeamDto[]>("/admin/teams"),
  });
  const reload = () => queryClient.invalidateQueries({ queryKey: ["admin-teams"] });
  const showCredential = (value: TeamWithPasswordDto) => {
    setCredential(value);
    requestAnimationFrame(() => {
      credentialPanel.current?.focus();
      credentialPanel.current?.scrollIntoView({ behavior: "instant", block: "nearest" });
    });
  };
  const reset = useMutation({
    mutationFn: (id: string) =>
      api.post<TeamWithPasswordDto>(`/admin/teams/${id}/reset-password`),
    onSuccess: showCredential,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/teams/${id}`),
    onSuccess: () => {
      setCredential(null);
      void reload();
    },
  });
  const official = useMutation({
    mutationFn: ({ id, isOfficial }: { id: string; isOfficial: boolean }) =>
      api.patch<TeamDto>(`/admin/teams/${id}`, { isOfficial }),
    onSuccess: () => void reload(),
  });
  const list = teams.data ?? [];
  const term = search.trim().toLocaleLowerCase("pt-BR");
  const filtered = list.filter((team) =>
    [team.name, team.username, ...team.members].some((value) =>
      value.toLocaleLowerCase("pt-BR").includes(term),
    ),
  );
  const busy = reset.isPending || remove.isPending || official.isPending;
  const clearErrors = () => {
    reset.reset();
    remove.reset();
    official.reset();
  };
  const columns: TableColumn<TeamDto>[] = [
    {
      key: "name",
      header: "Equipe",
      width: "220px",
      cell: (team) => (
        <div className="flex min-w-0 items-center gap-3">
          <Identicon
            seed={team.id}
            className="size-8 border border-border"
            title={`Simbolo da equipe ${team.name}`}
          />
          <span className="truncate font-medium" title={team.name}>
            {team.name}
          </span>
        </div>
      ),
    },
    {
      key: "username",
      header: "Usuário",
      width: "150px",
      cell: (team) => (
        <span className="text-muted-foreground" title={team.username}>
          {team.username}
        </span>
      ),
    },
    {
      key: "members",
      header: "Integrantes",
      // Sem largura de proposito: e a coluna que absorve o espaco restante.
      // Com TODAS as colunas fixas, a coluna-preenchimento do colgroup ficava
      // com 538px vazios e o cabecalho parecia terminar no meio da tabela.
      cell: (team) => (
        <span className="text-muted-foreground" title={team.members.join(", ")}>
          {team.members.join(", ") || "Não informados"}
        </span>
      ),
    },
    {
      key: "isOfficial",
      header: "Oficial",
      width: "100px",
      cell: (team) => (
        <Switch
          checked={team.isOfficial}
          ariaLabel={`Equipe oficial: ${team.name}`}
          disabled={busy}
          onCheckedChange={(isOfficial) => {
            clearErrors();
            official.mutate({ id: team.id, isOfficial });
          }}
          className="[&_button[data-state=checked]]:bg-accent"
        />
      ),
    },
    {
      key: "actions",
      header: "Ações",
      width: "130px",
      align: "right",
      cell: (team) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-10"
            aria-label={`Gerar nova senha para ${team.name}`}
            title="Gerar nova senha"
            disabled={busy || !!credential}
            onClick={async () => {
              if (
                await confirmAlert(
                  `Gerar nova senha para "${team.name}"? A senha anterior deixará de funcionar.`,
                )
              ) {
                clearErrors();
                reset.mutate(team.id);
              }
            }}
          >
            <KeyRound size={16} aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-10 text-destructive hover:text-destructive"
            aria-label={`Excluir ${team.name}`}
            title="Excluir equipe"
            disabled={busy}
            onClick={async () => {
              if (
                await confirmAlert(
                  `Excluir "${team.name}"? Equipes com submissões não podem ser excluídas.`,
                )
              ) {
                clearErrors();
                remove.mutate(team.id);
              }
            }}
          >
            <Trash2 size={16} aria-hidden />
          </Button>
        </div>
      ),
    },
  ];
  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[28px] font-semibold tracking-tight">Equipes</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Organize os participantes e gerencie os acessos à competição.
          </p>
        </div>
        <CreateTeam
          onCreated={reload}
          onCredential={showCredential}
          credentialOpen={!!credential}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: "Equipes cadastradas", value: list.length, icon: Users },
          {
            label: "Equipes oficiais",
            value: list.filter((t) => t.isOfficial).length,
            icon: UserRoundCheck,
          },
          {
            label: "Integrantes",
            value: list.reduce((total, t) => total + t.members.length, 0),
            icon: UserRound,
          },
        ].map(({ label, value, icon: Icon }) => (
          <section key={label} className="rounded-2xl border border-border bg-card p-5">
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon size={16} aria-hidden />
              {label}
            </p>
            <p className="mt-3 text-3xl font-semibold tabular-nums">
              {teams.isPending || teams.isError ? "—" : value}
            </p>
          </section>
        ))}
      </div>

      {credential && (
        <div
          ref={credentialPanel}
          tabIndex={-1}
          aria-labelledby="credential-title"
          className="rounded-2xl border border-accent/30 bg-card p-5"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="credential-title" className="flex items-center gap-2 font-semibold">
                <ShieldCheck size={18} className="text-accent" aria-hidden />
                Acesso de {credential.team.name}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Anote agora. Esta é a única exibição da senha gerada.
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Fechar credenciais"
              onClick={() => setCredential(null)}
            >
              <X size={17} />
            </Button>
          </div>
          <dl className="my-4 grid gap-4 rounded-xl border border-border bg-background p-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-muted-foreground">Usuário</dt>
              <dd className="mt-1 break-all font-mono text-sm">
                {credential.team.username}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Senha</dt>
              <dd className="mt-1 break-all font-mono text-sm">{credential.password}</dd>
            </div>
          </dl>
          <Button
            variant="secondary"
            className="rounded-xl"
            onClick={() => setCredential(null)}
          >
            <Check size={16} aria-hidden />
            Já anotei
          </Button>
        </div>
      )}

      <section
        className="min-w-0 overflow-hidden rounded-2xl border border-border bg-card"
        aria-labelledby="teams-title"
      >
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border p-5">
          <div>
            <h2 id="teams-title" className="text-base font-semibold">
              Equipes participantes
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Gerencie a participação e as credenciais de cada equipe.
            </p>
          </div>
          <Input
            aria-label="Buscar equipes"
            placeholder="Buscar equipe, usuário ou integrante"
            value={search}
            onChange={setSearch}
            leftIcon={<Search aria-hidden />}
            className="w-full sm:w-80"
            classNames={fields}
          />
        </div>
        {(reset.error || remove.error || official.error) && (
          <div className="p-4">
            <Feedback error>
              {errorMessage(reset.error ?? remove.error ?? official.error)}
            </Feedback>
          </div>
        )}
        {teams.error ? (
          <div className="space-y-4 p-5">
            <Feedback error>{errorMessage(teams.error)}</Feedback>
            <Button variant="secondary" onClick={() => void teams.refetch()}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <div role="region" aria-label="Lista de equipes" tabIndex={0}>
            <Table
              data={filtered}
              columns={columns}
              getRowId={(team) => team.id}
              loading={teams.isPending}
              rowHeight={64}
              height={tableHeight(filtered.length)}
              className="rounded-none border-0 bg-transparent"
              emptyState={
                <div className="space-y-2">
                  <Users
                    size={24}
                    className="mx-auto text-muted-foreground"
                    aria-hidden
                  />
                  <p className="font-medium text-foreground">
                    {term ? "Nenhuma equipe encontrada" : "Nenhuma equipe cadastrada"}
                  </p>
                  <p className="text-xs">
                    {term
                      ? "Tente outro nome, usuário ou integrante."
                      : "Use o botão Nova equipe para cadastrar os participantes."}
                  </p>
                </div>
              }
            />
          </div>
        )}
        <div className="flex flex-wrap justify-between gap-2 border-t border-border px-5 py-4 text-xs text-muted-foreground">
          <span role="status">
            {teams.isPending
              ? "Carregando equipes…"
              : teams.isError
                ? "Listagem indisponível"
                : `${filtered.length} de ${list.length} equipes`}
          </span>
          <span>Equipes não oficiais aparecem no placar, mas não ocupam posição.</span>
        </div>
      </section>
    </div>
  );
}

function CreateTeam({
  onCreated,
  onCredential,
  credentialOpen,
}: {
  onCreated: () => Promise<void>;
  onCredential: (value: TeamWithPasswordDto) => void;
  credentialOpen: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [members, setMembers] = useState("");
  const create = useMutation({
    mutationFn: () =>
      api.post<TeamWithPasswordDto>("/admin/teams", {
        name,
        username,
        members: members
          .split(",")
          .map((m) => m.trim())
          .filter(Boolean),
      }),
    onSuccess: (result) => {
      setOpen(false);
      onCredential(result);
      setName("");
      setUsername("");
      setMembers("");
      void onCreated();
    },
  });
  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!create.isPending) setOpen(next);
    },
    [create.isPending],
  );
  return (
    <CenterMorphModal open={open} onOpenChange={onOpenChange}>
      <CenterMorphModalTrigger>
        <Button
          className="rounded-xl"
          disabled={credentialOpen}
          title={
            credentialOpen
              ? "Anote e feche as credenciais antes de criar outra equipe."
              : undefined
          }
        >
          <Plus size={16} aria-hidden />
          Nova equipe
        </Button>
      </CenterMorphModalTrigger>
      <CenterMorphModalContent
        ariaLabel="Nova equipe"
        ariaDescribedBy="create-team-description"
        closeButtonLabel="Fechar cadastro"
        dismissible={!create.isPending}
        showCloseButton={!create.isPending}
        className="admin-shell max-w-lg max-h-[calc(100dvh-4rem)] overflow-y-auto bg-card"
        backdropClassName="bg-background/70"
      >
        <div className="border-b border-border p-6">
          <h2 id="create-team-title" className="flex items-center gap-2 font-semibold">
            <Plus size={18} className="text-muted-foreground" aria-hidden />
            Nova equipe
          </h2>
          <p id="create-team-description" className="mt-2 text-sm text-muted-foreground">
            Cadastre uma equipe e gere suas credenciais de acesso.
          </p>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!create.isPending && !credentialOpen) create.mutate();
          }}
        >
          <div className="space-y-4 p-6">
            <Input
              label="Nome da equipe"
              placeholder="Ex.: Código Nerd"
              value={name}
              onChange={setName}
              maxLength={120}
              required
              disabled={create.isPending}
              classNames={fields}
            />
            <Input
              label="Usuário"
              placeholder="Ex.: equipe01"
              value={username}
              onChange={setUsername}
              minLength={3}
              maxLength={32}
              pattern="[a-z0-9][a-z0-9._\-]*"
              title="De 3 a 32 caracteres: minúsculas, números, ponto, hífen ou sublinhado."
              autoCapitalize="none"
              autoComplete="off"
              spellCheck={false}
              required
              disabled={create.isPending}
              classNames={fields}
            />
            <p className="text-xs text-muted-foreground">
              Use minúsculas, números, ponto, hífen ou sublinhado.
            </p>
            <Input
              label="Integrantes"
              placeholder="Ex.: Ana Silva, Pedro Lima"
              value={members}
              onChange={setMembers}
              disabled={create.isPending}
              classNames={fields}
            />
            <p className="text-xs text-muted-foreground">
              Separe os nomes por vírgula. Até 10 integrantes.
            </p>
            {create.error && <Feedback error>{errorMessage(create.error)}</Feedback>}
            {credentialOpen && (
              <p className="text-xs text-muted-foreground">
                Anote e feche as credenciais acima antes de criar outra equipe.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
            <CenterMorphModalClose>
              <Button
                variant="secondary"
                className="rounded-xl"
                disabled={create.isPending}
              >
                Cancelar
              </Button>
            </CenterMorphModalClose>
            <Button
              type="submit"
              className="rounded-xl"
              disabled={
                create.isPending || credentialOpen || !name.trim() || !username.trim()
              }
            >
              <Plus size={15} aria-hidden />
              {create.isPending ? "Criando…" : "Criar equipe"}
            </Button>
          </div>
        </form>
      </CenterMorphModalContent>
    </CenterMorphModal>
  );
}
